import ArticlePage from './pages/ArticlePage';
import RegulationFeedPage from './pages/RegulationFeedPage';
import CategoryPage from './pages/ContentIndexPage';
import SiteContainer from './components/Containers/SiteContainer';
import AuthorPage from './pages/AuthorPage';
import FrontPage from './pages/FrontPage';
import ArticlePageContainer from './components/Article/ArticlePageContainer';
import GeneralPageContainer from './components/Containers/GeneralPageContainer';
import {
  getNewContentIndexPathFromOld,
  getRouterPathToArticle,
  isDeprecatedContentIndexRoute,
  JaaRoute,
} from './utilities/Links';
import AuthorListPage from './pages/AuthorListPage';
import { LoginCallback } from './components/Auth/LoginCallBack';
import { SearchPage } from './pages/SearchPage';
import NewsIndexPage from './pages/NewsIndexPage';
import { ContentNotFound } from './components/ErrorViews/ContentNotFound';
import { ReaderRoot, Reader } from '@ailex/reading-view';
import { AuthContext } from './components/Auth/AuthProvider';
import { useContext } from 'react';
import { FeatureFlagAdmin } from './features/Admin/components/FeatureFlagAdmin';
import { redirect, RouteObject } from 'react-router';
import SsoLoginRedirect from './components/Auth/SsoLoginRedirect';
import RedirectOnlyComponent from './components/RedirectOnlyComponent';

const HTTP_PERMANENT_REDIRECT = 308;

const ReaderWrapper = () => {
  const userAuthenticationContext = useContext(AuthContext);

  return <ReaderRoot identityContext={userAuthenticationContext} />;
};

const Routes: RouteObject[] = [
  {
    Component: SiteContainer,
    HydrateFallback: RedirectOnlyComponent,
    children: [
      {
        Component: ArticlePageContainer,
        children: [
          {
            path: JaaRoute.articleShow + ':id',
            Component: ArticlePage,
          },
        ],
      },
      {
        Component: GeneralPageContainer,
        children: [
          { path: JaaRoute.frontPage, Component: FrontPage },
          { path: JaaRoute.authorListPage, Component: AuthorListPage },
          { path: JaaRoute.newsIndex, Component: NewsIndexPage },
          { path: JaaRoute.contentIndex, Component: CategoryPage },
          { path: JaaRoute.regulationIndex, Component: RegulationFeedPage },
          { path: JaaRoute.authorShow + ':author', Component: AuthorPage },
          { path: JaaRoute.ssoLoginRedirect, Component: SsoLoginRedirect },
          {
            path: JaaRoute.frontPageAfterLogout,
            loader: () => {
              const originalUrl = sessionStorage.getItem(
                'original-url-before-redirect',
              );
              return redirect(originalUrl ?? JaaRoute.frontPage);
            },
          },
          { path: JaaRoute.articleSearch, Component: SearchPage },

          // Add old routes here for backwards compatibility
          {
            path: '/tekijat',
            Component: RedirectOnlyComponent,
            loader: () =>
              redirect(JaaRoute.authorListPage, {
                status: HTTP_PERMANENT_REDIRECT,
              }),
          },
          {
            path: '/tekija/:id',
            Component: RedirectOnlyComponent,
            loader: ({ params }) =>
              redirect(JaaRoute.authorShow + params.id, {
                status: HTTP_PERMANENT_REDIRECT,
              }),
          },
          {
            path: '/sisallot/:id',
            Component: RedirectOnlyComponent,
            loader: ({ params, request }) => {
              /* Check if the URL is a deprecated content index route, redirect
              to the correct content index route if necessary. */
              if (isDeprecatedContentIndexRoute(request.url, params.id)) {
                return redirect(
                  getNewContentIndexPathFromOld(request.url, params.id),
                  { status: HTTP_PERMANENT_REDIRECT },
                );
              }

              // If the URL is not a deprecated content index route, show the article
              return redirect(getRouterPathToArticle(params.id ?? ''), {
                status: HTTP_PERMANENT_REDIRECT,
              });
            },
          },
          {
            path: '/seuranta/:id',
            Component: RedirectOnlyComponent,
            loader: ({ params }) => {
              const categoryParam = params.id;
              const searchParams = new URLSearchParams();

              if (categoryParam) {
                searchParams.set('category', categoryParam);
              }

              return redirect(
                `${JaaRoute.regulationIndex}?${searchParams.toString()}`,
                { status: HTTP_PERMANENT_REDIRECT },
              );
            },
          },

          // Catch all other undefined routes
          { path: '*', Component: ContentNotFound },
        ],
      },
      {
        path: '/admin/feature-flags',
        Component: FeatureFlagAdmin,
      },
    ],
  },
  // Top-level routes without SiteContainer
  { path: JaaRoute.frontPageAfterLogin, Component: LoginCallback },
  {
    Component: ReaderWrapper,
    HydrateFallback: RedirectOnlyComponent,
    children: [
      {
        path: '/lue/:isbn',
        Component: Reader,
      },
    ],
  },
];

export default Routes;
